import { useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { cx } from './cx';

/**
 * Form primitives.
 *
 * `scanner` raises the font to 16px and the height to 44px. 16px is the point
 * below which iOS zooms the page on focus — unusable while scanning barcodes at
 * the counter — and 44px is the minimum comfortable touch target (SKILL.md).
 */

interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}

export function Field({ label, hint, error, htmlFor, className, children }: FieldProps) {
  return (
    <div className={cx('field', className)}>
      {label && <label htmlFor={htmlFor}>{label}</label>}
      {children}
      {error ? (
        <span className="field-error">{error}</span>
      ) : (
        hint && <span className="field-hint">{hint}</span>
      )}
    </div>
  );
}

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  scanner?: boolean;
  invalid?: boolean;
}

export function TextInput({ scanner, invalid, className, ...rest }: TextInputProps) {
  return (
    <input
      className={cx('input', scanner && 'input-lg', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
}

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export function TextArea({ invalid, className, ...rest }: TextAreaProps) {
  return (
    <textarea className={cx('input', className)} aria-invalid={invalid || undefined} {...rest} />
  );
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  scanner?: boolean;
  invalid?: boolean;
  children: ReactNode;
}

export function Select({ scanner, invalid, className, children, ...rest }: SelectProps) {
  return (
    <select
      className={cx('input', scanner && 'input-lg', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    >
      {children}
    </select>
  );
}

/** Field + input wired together with a generated id, the common case. */
export function LabeledInput({
  label,
  hint,
  error,
  className,
  ...inputProps
}: TextInputProps & Pick<FieldProps, 'label' | 'hint' | 'error'>) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id} className={className}>
      <TextInput id={id} invalid={Boolean(error)} {...inputProps} />
    </Field>
  );
}
